/**
 * DeliveryMap Component
 * Shows vendor and buyer locations with OSRM road route for active deliveries
 * Includes full turn-by-turn navigation mode like Google Maps/Grab
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// No custom CSS - let Leaflet handle its own styling

interface DeliveryMapProps {
  vendorLocation: { lat: number; lng: number };
  buyerLocation: { lat: number; lng: number };
  vendorName: string;
  buyerAddress: string;
  riderLocation?: { lat: number; lng: number; bearing?: number } | null;
  orderStatus?: string; // Used to determine routing: before pickup → Rider to Vendor, after → Rider to Buyer
}

// Turn instruction from OSRM
interface TurnInstruction {
  type: string;
  modifier?: string;
  name: string;
  distance: number;
  duration: number;
}

// Get turn instruction icon based on type and modifier
function getTurnIcon(type: string, modifier?: string): string {
  const icons: Record<string, string> = {
    'depart': '🚀',
    'arrive': '🏁',
    'turn-left': '⬅️',
    'turn-right': '➡️',
    'turn-slight left': '↖️',
    'turn-slight right': '↗️',
    'turn-sharp left': '⤴️',
    'turn-sharp right': '⤵️',
    'continue': '⬆️',
    'straight': '⬆️',
    'merge': '⤴️',
    'roundabout': '🔄',
    'rotary': '🔄',
    'fork-left': '↖️',
    'fork-right': '↗️',
    'end of road-left': '⬅️',
    'end of road-right': '➡️',
    'uturn': '↩️',
  };
  
  const key = modifier ? `${type}-${modifier}` : type;
  return icons[key] || icons[type] || '⬆️';
}



// Fix Leaflet default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Decode OSRM polyline geometry
function decodePolyline(encoded: string): [number, number][] {
  const points: [number, number][] = [];
  let index = 0, lat = 0, lng = 0;

  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += dlng;

    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
}

export default function DeliveryMap({ 
  vendorLocation, 
  buyerLocation, 
  vendorName, 
  buyerAddress,
  riderLocation,
  orderStatus = 'rider_enroute'
}: DeliveryMapProps) {
  // Determine if we've picked up the order - route changes after pickup
  const hasPickedUp = ['picked_up', 'out_for_delivery', 'delivered'].includes(orderStatus);
  
  // Determine destination based on order status
  const destination = hasPickedUp ? buyerLocation : vendorLocation;
  const destinationLabel = hasPickedUp ? 'Delivery' : 'Pickup';
  const destinationName = hasPickedUp ? buyerAddress : vendorName;
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const routeLineRef = useRef<L.Polyline | null>(null);
  const riderMarkerRef = useRef<L.Marker | null>(null);
  const [routeInfo, setRouteInfo] = useState<{ distance: number; duration: number } | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Navigation mode state
  const [isNavigating, setIsNavigating] = useState(false); // True = turn-by-turn navigation mode
  const [isFollowing, setIsFollowing] = useState(true); // Auto-follow rider position
  const [turnInstructions, setTurnInstructions] = useState<TurnInstruction[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const lastBearingRef = useRef<number>(0);

  // Handle fullscreen toggle - single delayed invalidateSize (multiple calls cancel tile requests)
  useEffect(() => {
    if (!mapRef.current || !mapContainer.current) return;
    
    // Single invalidateSize after CSS transitions complete
    const timer = setTimeout(() => {
      if (!mapRef.current) return;
      
      // Just invalidate size - don't call redraw() as it cancels tile requests
      mapRef.current.invalidateSize({ animate: false, pan: false });
      
      // Fit bounds to show all markers
      if (vendorLocation && buyerLocation) {
        const bounds = L.latLngBounds([
          [vendorLocation.lat, vendorLocation.lng],
          [buyerLocation.lat, buyerLocation.lng],
        ]);
        if (riderLocation) {
          bounds.extend([riderLocation.lat, riderLocation.lng]);
        }
        mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
      }
    }, 400);
    
    return () => clearTimeout(timer);
  }, [isFullscreen, vendorLocation, buyerLocation, riderLocation]);

  // Fetch OSRM route with turn-by-turn instructions
  // Route: Rider → Vendor (before pickup) OR Rider → Buyer (after pickup)
  useEffect(() => {
    if (!mapReady) return;
    if (!vendorLocation?.lat || !vendorLocation?.lng || !buyerLocation?.lat || !buyerLocation?.lng) return;
    if (!routeLineRef.current) return;

    const fetchRoute = async () => {
      try {
        // Determine origin: use rider location if available, otherwise vendor
        const origin = riderLocation?.lat && riderLocation?.lng 
          ? riderLocation 
          : vendorLocation;
        
        // Call OSRM for road route WITH turn-by-turn steps
        const osrmUrl = `http://localhost:5000/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=polyline&steps=true&annotations=true`;
        console.log('[DeliveryMap] Fetching OSRM route:', osrmUrl, '| Status:', orderStatus, '| To:', destinationLabel);
        
        const response = await fetch(osrmUrl);
        const data = await response.json();
        console.log('[DeliveryMap] OSRM response:', data.code);

        if (data.code === 'Ok' && data.routes?.[0]) {
          const route = data.routes[0];
          const routeCoords = decodePolyline(route.geometry);
          console.log('[DeliveryMap] Route decoded, points:', routeCoords.length);
          
          // Extract turn-by-turn instructions from steps
          if (route.legs?.[0]?.steps) {
            const steps: TurnInstruction[] = route.legs[0].steps.map((step: any) => ({
              type: step.maneuver?.type || 'continue',
              modifier: step.maneuver?.modifier,
              name: step.name || step.ref || '',
              distance: step.distance || 0,
              duration: step.duration || 0,
            }));
            setTurnInstructions(steps);
            console.log('[DeliveryMap] Turn instructions:', steps.length, 'steps');
          }
          
          // Update route line with actual road geometry
          if (routeLineRef.current) {
            routeLineRef.current.setLatLngs(routeCoords);
            // Thicker line for navigation mode
            routeLineRef.current.setStyle({
              color: '#3b82f6', // Blue like Google Maps
              weight: isNavigating ? 8 : 5,
              opacity: 0.9,
              dashArray: undefined,
            });
            console.log('[DeliveryMap] Route line updated with OSRM geometry');
          }

          setRouteInfo({
            distance: Math.round(route.distance), // meters
            duration: Math.round(route.duration / 60), // minutes
          });
        } else {
          console.warn('[DeliveryMap] OSRM returned non-Ok response:', data);
        }
      } catch (error) {
        console.warn('[DeliveryMap] OSRM route fetch failed, using straight line:', error);
        // Keep the straight dashed line as fallback
      }
    };

    fetchRoute();
  }, [mapReady, vendorLocation, buyerLocation, riderLocation, destination, orderStatus, destinationLabel, isNavigating]);
  
  // Auto-follow rider in navigation mode
  useEffect(() => {
    if (!isNavigating || !isFollowing || !riderLocation || !mapRef.current) return;
    
    // Center on rider with navigation zoom level - zoom 15 for better context
    mapRef.current.setView(
      [riderLocation.lat, riderLocation.lng],
      15, // Zoom 15 gives better street context for navigation
      { animate: true, duration: 0.5 }
    );
    
    // Rotate map based on bearing if available
    if (riderLocation.bearing !== undefined) {
      lastBearingRef.current = riderLocation.bearing;
    }
  }, [isNavigating, isFollowing, riderLocation]);
  
  // Fit map bounds when entering navigation mode
  useEffect(() => {
    if (!mapRef.current || !mapContainer.current || !isNavigating) return;
    
    const fitNavigationBounds = () => {
      try {
        if (!mapRef.current || !riderLocation || !destination) return;
        
        const bounds = L.latLngBounds([
          [riderLocation.lat, riderLocation.lng],
          [destination.lat, destination.lng],
        ]);
        mapRef.current.fitBounds(bounds, { 
          padding: [100, 100],
          maxZoom: 16 
        });
      } catch (e) {
        console.warn('[DeliveryMap] Navigation bounds error:', e);
      }
    };
    
    // Single delayed call
    const timer = setTimeout(fitNavigationBounds, 500);
    return () => clearTimeout(timer);
  }, [isNavigating, destination]);
  
  // Detect when user pans away (disable follow mode)
  useEffect(() => {
    if (!mapRef.current || !isNavigating) return;
    
    const handleMoveStart = () => {
      // User started dragging - disable follow
      setIsFollowing(false);
    };
    
    mapRef.current.on('dragstart', handleMoveStart);
    
    return () => {
      mapRef.current?.off('dragstart', handleMoveStart);
    };
  }, [isNavigating]);
  
  // Recenter function - shows rider position with good context
  const handleRecenter = useCallback(() => {
    if (!riderLocation || !mapRef.current) return;
    setIsFollowing(true);
    
    // If we have destination, fit to show both points
    if (destination) {
      const bounds = L.latLngBounds([
        [riderLocation.lat, riderLocation.lng],
        [destination.lat, destination.lng],
      ]);
      mapRef.current.fitBounds(bounds, { 
        padding: [100, 100],
        maxZoom: 16,
        animate: true
      });
    } else {
      mapRef.current.setView(
        [riderLocation.lat, riderLocation.lng],
        15,
        { animate: true }
      );
    }
  }, [riderLocation, destination]);
  
  // Start navigation mode
  const startNavigation = useCallback(() => {
    setIsNavigating(true);
    setIsFullscreen(true);
    setIsFollowing(true);
    setCurrentStepIndex(0);
    // The useEffect for isFullscreen will handle the map redraw
  }, []);
  
  // Exit navigation mode
  const exitNavigation = useCallback(() => {
    setIsNavigating(false);
    setIsFullscreen(false);
    setIsFollowing(false);
  }, []);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    // Validate coordinates
    if (!vendorLocation?.lat || !vendorLocation?.lng || !buyerLocation?.lat || !buyerLocation?.lng) {
      console.warn('DeliveryMap: Invalid coordinates');
      return;
    }

    // Create map centered between vendor and buyer
    const centerLat = (vendorLocation.lat + buyerLocation.lat) / 2;
    const centerLng = (vendorLocation.lng + buyerLocation.lng) / 2;

    mapRef.current = L.map(mapContainer.current, {
      center: [centerLat, centerLng],
      zoom: 14,
      zoomControl: true,
    });

    // Add map tiles immediately
    tileLayerRef.current = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
      subdomains: ['a', 'b', 'c'],
    }).addTo(mapRef.current);
    
    console.log('[DeliveryMap] Tile layer added');
    
    console.log('[DeliveryMap] Tile layer added to map');

    // Vendor marker (green)
    const vendorIcon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background-color: #22c55e;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
      ">🏪</div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });

    L.marker([vendorLocation.lat, vendorLocation.lng], { icon: vendorIcon })
      .bindPopup(`<div style="padding: 4px;"><strong>Pickup</strong><br/>${vendorName}</div>`, { closeButton: false })
      .addTo(mapRef.current);

    // Buyer marker (red)
    const buyerIcon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background-color: #ef4444;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
      ">🏠</div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });

    L.marker([buyerLocation.lat, buyerLocation.lng], { icon: buyerIcon })
      .bindPopup(`<div style="padding: 4px;"><strong>Delivery</strong><br/>${buyerAddress}</div>`, { closeButton: false })
      .addTo(mapRef.current);

    // Draw initial straight route line (will be replaced with OSRM route)
    routeLineRef.current = L.polyline(
      [
        [vendorLocation.lat, vendorLocation.lng],
        [buyerLocation.lat, buyerLocation.lng],
      ],
      {
        color: '#3b82f6',
        weight: 4,
        opacity: 0.7,
        dashArray: '10, 10', // Dashed = straight line fallback
      }
    ).addTo(mapRef.current);

    // Fit bounds to show both markers
    const bounds = L.latLngBounds([
      [vendorLocation.lat, vendorLocation.lng],
      [buyerLocation.lat, buyerLocation.lng],
    ]);
    mapRef.current.fitBounds(bounds, { padding: [40, 40] });

    // Signal that map is ready for OSRM route fetch
    setMapReady(true);
    console.log('[DeliveryMap] Map initialized, ready for OSRM route');

    // Single invalidateSize after tiles start loading
    const timer = setTimeout(() => mapRef.current?.invalidateSize(), 200);

    return () => {
      clearTimeout(timer);
      mapRef.current?.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [vendorLocation, buyerLocation, vendorName, buyerAddress]);

  // Update rider position with motorcycle icon
  useEffect(() => {
    if (!mapRef.current || !riderLocation?.lat || !riderLocation?.lng) return;

    // Motorcycle icon for rider
    const riderIcon = L.divIcon({
      className: 'custom-marker rider-marker',
      html: `<div style="
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background-color: #3b82f6;
        border: 4px solid white;
        box-shadow: 0 4px 15px rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
      ">🏍️</div>`,
      iconSize: [44, 44],
      iconAnchor: [22, 22],
    });
    
    if (riderMarkerRef.current) {
      riderMarkerRef.current.setLatLng([riderLocation.lat, riderLocation.lng]);
      riderMarkerRef.current.setIcon(riderIcon);
    } else {
      riderMarkerRef.current = L.marker([riderLocation.lat, riderLocation.lng], { icon: riderIcon })
        .bindPopup('<strong>You are here</strong>', { closeButton: false })
        .addTo(mapRef.current);
    }
  }, [riderLocation]);

  // Validate coordinates
  if (!vendorLocation?.lat || !vendorLocation?.lng || !buyerLocation?.lat || !buyerLocation?.lng) {
    return (
      <div className="h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">
        Map unavailable - missing location data
      </div>
    );
  }

  // Fullscreen wrapper - locks to viewport edges with solid background to block content
  const wrapperClasses = isFullscreen 
    ? 'fixed inset-0 z-[9999] bg-gray-100'
    : 'relative';

  // Map container styles - use viewport units in fullscreen for reliable sizing
  const mapClasses = isFullscreen
    ? ''
    : 'h-48 w-full rounded-lg overflow-hidden border border-border';
  
  // Explicit dimensions for fullscreen mode
  const mapStyle: React.CSSProperties | undefined = isFullscreen 
    ? { width: '100vw', height: '100vh' } 
    : undefined;

  // Get current and next turn instruction
  const currentStep = turnInstructions[currentStepIndex];
  const nextStep = turnInstructions[currentStepIndex + 1];

  return (
    <div className={wrapperClasses}>
      {/* Map Container - positioned to fill entire wrapper */}
      <div 
        ref={mapContainer} 
        className={mapClasses}
        style={mapStyle}
      />
      
      {/* NAVIGATION MODE - Google Maps style overlay */}
      {isFullscreen && isNavigating && currentStep && (
        <>
          {/* Turn Instruction Banner - Top (Green like Google Maps) */}
          <div className="absolute top-0 left-0 right-0 z-[100] bg-green-700 text-white shadow-lg">
            {/* Main instruction */}
            <div className="px-5 py-4 flex items-center gap-4">
              {/* Turn Icon - Large */}
              <div className="text-5xl">
                {getTurnIcon(currentStep.type, currentStep.modifier)}
              </div>
              <div className="flex-1 min-w-0">
                {/* Distance to next turn */}
                <div className="text-3xl font-bold">
                  {currentStep.distance >= 1000 
                    ? `${(currentStep.distance / 1000).toFixed(1)} km` 
                    : `${Math.round(currentStep.distance)} m`}
                </div>
                {/* Instruction text */}
                <div className="text-lg opacity-95 truncate">
                  {currentStep.type === 'arrive' 
                    ? 'Arrive at destination'
                    : currentStep.name 
                      ? `${currentStep.modifier === 'left' ? 'Turn left on' : currentStep.modifier === 'right' ? 'Turn right on' : 'Continue on'} ${currentStep.name}`
                      : 'Continue on current road'}
                </div>
              </div>
            </div>
            
            {/* Then preview - like Google Maps */}
            {nextStep && (
              <div className="px-5 py-2 bg-green-800 flex items-center gap-3">
                <span className="bg-green-600 px-2 py-1 rounded text-sm font-bold">Then</span>
                <span className="text-xl">{getTurnIcon(nextStep.type, nextStep.modifier)}</span>
                <span className="text-base truncate">{nextStep.name || 'continue'}</span>
              </div>
            )}
          </div>
          
          {/* Right side controls - zoom + compass area */}
          <div className="absolute right-3 top-1/3 z-[100] flex flex-col gap-2">
            <button
              onClick={() => mapRef.current?.zoomIn()}
              className="bg-white text-gray-700 w-11 h-11 rounded-lg shadow-md flex items-center justify-center text-xl font-bold hover:bg-gray-100 transition-colors"
            >
              +
            </button>
            <button
              onClick={() => mapRef.current?.zoomOut()}
              className="bg-white text-gray-700 w-11 h-11 rounded-lg shadow-md flex items-center justify-center text-xl font-bold hover:bg-gray-100 transition-colors"
            >
              −
            </button>
          </div>
        </>
      )}
      
      {/* FULLSCREEN MODE (non-navigation) - Simple header overlaid */}
      {isFullscreen && !isNavigating && (
        <div className={`absolute top-0 left-0 right-0 z-[100] ${hasPickedUp ? 'bg-blue-600/95' : 'bg-green-600/95'} backdrop-blur text-white px-4 py-3 flex items-center justify-between shadow-lg`}>
          <button
            onClick={() => setIsFullscreen(false)}
            className={`p-2 -ml-2 ${hasPickedUp ? 'hover:bg-blue-700' : 'hover:bg-green-700'} rounded-lg transition-colors`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="text-center flex-1">
            <div className="font-bold text-lg">
              {hasPickedUp ? '🚴 Delivering to Customer' : '📦 Heading to Pickup'}
            </div>
            <div className={`${hasPickedUp ? 'text-blue-100' : 'text-green-100'} text-sm`}>
              {destinationName}
            </div>
          </div>
          <div className="w-10"></div>
        </div>
      )}

      {/* Route Info Bar - Fullscreen non-navigation - overlaid at top below header */}
      {isFullscreen && !isNavigating && routeInfo && (
        <div className="absolute top-16 left-4 z-[100] bg-gray-900/90 backdrop-blur text-white px-4 py-3 rounded-xl flex items-center gap-4 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="bg-green-500 rounded-full p-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <div className="font-bold text-xl">{routeInfo.duration} min</div>
              <div className="text-gray-400 text-sm">{(routeInfo.distance / 1000).toFixed(1)} km</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-gray-400 text-sm">ETA</div>
            <div className="font-medium">
              {new Date(Date.now() + routeInfo.duration * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      )}

      {/* Regular view controls - high z-index to stay above Leaflet controls */}
      {!isFullscreen && (
        <>
          {/* START NAVIGATION button - Large and prominent */}
          <button
            onClick={startNavigation}
            className="absolute top-16 left-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl px-4 py-3 shadow-lg transition-colors z-[1000] flex items-center gap-2 font-bold text-base"
            title="Start turn-by-turn navigation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Start
          </button>
          {/* Legend */}
          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur rounded-lg px-2 py-1 text-xs flex gap-3 shadow z-[1000]">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              Pickup
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              Delivery
            </span>
            {riderLocation && (
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                You
              </span>
            )}
          </div>
          {/* Route info badge */}
          {routeInfo && (
            <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-lg px-2 py-1 text-xs font-medium shadow z-[1000]">
              {(routeInfo.distance / 1000).toFixed(1)} km • {routeInfo.duration} min
            </div>
          )}
        </>
      )}

      {/* NAVIGATION MODE - Bottom Bar (Google Maps style) */}
      {isFullscreen && isNavigating && (
        <div className="absolute bottom-0 left-0 right-0 z-[100] bg-white shadow-lg rounded-t-2xl">
          {/* ETA and distance row */}
          <div className="px-5 py-3 flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900">
                  {routeInfo ? `${routeInfo.duration} min` : '--'}
                </span>
                <span className="text-green-600 text-sm">●</span>
              </div>
              <div className="text-gray-500 text-sm">
                {routeInfo ? `${(routeInfo.distance / 1000).toFixed(1)} km` : '--'} · ETA {routeInfo 
                  ? new Date(Date.now() + routeInfo.duration * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : '--:--'}
              </div>
            </div>
            
            {/* Recenter button */}
            <button
              onClick={handleRecenter}
              className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            
            {/* Exit button - prominent red like Google Maps */}
            <button
              onClick={exitNavigation}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-full font-bold text-base transition-colors"
            >
              Exit
            </button>
          </div>
          
          {/* Quick actions row */}
          <div className="px-4 pb-4 flex gap-2">
            {/* Full Route button */}
            <button
              onClick={() => {
                if (!mapRef.current || !riderLocation) return;
                setIsFollowing(false);
                const bounds = L.latLngBounds([
                  [riderLocation.lat, riderLocation.lng],
                  [destination.lat, destination.lng],
                ]);
                if (hasPickedUp) {
                  bounds.extend([vendorLocation.lat, vendorLocation.lng]);
                }
                mapRef.current.fitBounds(bounds, { 
                  padding: [80, 80],
                  maxZoom: 15
                });
              }}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-full font-medium text-sm flex items-center justify-center gap-1 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Route
            </button>
            
            {/* Open in Google Maps */}
            <button
              onClick={() => {
                const origin = riderLocation 
                  ? `${riderLocation.lat},${riderLocation.lng}`
                  : `${vendorLocation.lat},${vendorLocation.lng}`;
                const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination.lat},${destination.lng}&travelmode=driving`;
                window.open(url, '_blank');
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              Open Google Maps
            </button>
          </div>
        </div>
      )}
      
      {/* FULLSCREEN (non-navigation) Bottom Bar - overlaid */}
      {isFullscreen && !isNavigating && (
        <div className="absolute bottom-0 left-0 right-0 z-[100] bg-gray-900/95 backdrop-blur text-white px-4 py-4 shadow-lg">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-gray-400 text-xs mb-1">{destinationLabel.toUpperCase()}</div>
              <div className="font-medium truncate">{destinationName}</div>
            </div>
            {/* Start Navigation button */}
            <button
              onClick={startNavigation}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-bold flex items-center gap-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Start Nav
            </button>
            <button
              onClick={() => {
                const origin = riderLocation 
                  ? `${riderLocation.lat},${riderLocation.lng}`
                  : `${vendorLocation.lat},${vendorLocation.lng}`;
                const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination.lat},${destination.lng}&travelmode=driving`;
                window.open(url, '_blank');
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors shrink-0"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              Google
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

